import { useState, useEffect } from "react";
import { DEFAULT_SECTIONS } from "../../hooks/useDashboardSettings";

const DashboardCustomizer = ({ isOpen, sections, onSave, onClose }) => {
  const [draft, setDraft] = useState([]);

  // Sync draft every time the modal opens
  useEffect(() => {
    if (isOpen) setDraft(sections.map((s) => ({ ...s })));
  }, [isOpen, sections]);

  if (!isOpen) return null;

  const move = (index, dir) => {
    const next = [...draft];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next);
  };

  const toggle = (id) =>
    setDraft((prev) => prev.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s)));

  const handleReset = () => setDraft(DEFAULT_SECTIONS.map((s) => ({ ...s })));

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  const visibleCount = draft.filter((s) => s.visible).length;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1050,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "var(--bg-white)",
        borderRadius: "14px",
        width: "100%",
        maxWidth: "460px",
        maxHeight: "88vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
      }}>

        {/* Header */}
        <div style={{
          padding: "1.1rem 1.25rem",
          borderBottom: "1px solid var(--border-light)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <h6 style={{ margin: 0, fontWeight: 700 }}>Customise Dashboard</h6>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-light)" }}>
              {visibleCount} of {draft.length} sections visible
            </p>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} style={{ lineHeight: 1 }}>✕</button>
        </div>

        {/* Instructions */}
        <div style={{ padding: "0.75rem 1.25rem 0", fontSize: "0.82rem", color: "var(--text-light)" }}>
          Use the arrows to reorder. Toggle the switch to show or hide a section.
        </div>

        {/* Section list */}
        <div style={{ overflowY: "auto", flex: 1, padding: "0.75rem 1.25rem" }}>
          {draft.map((section, index) => (
            <div
              key={section.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                padding: "0.6rem 0.75rem",
                marginBottom: "0.4rem",
                borderRadius: "10px",
                border: "1px solid var(--border-light)",
                background: section.visible
                  ? "var(--bg-white)"
                  : "var(--bg-light, #f9f9f9)",
                opacity: section.visible ? 1 : 0.55,
                transition: "opacity 0.2s, background 0.2s",
              }}
            >
              {/* Up / Down */}
              <div style={{ display: "flex", flexDirection: "column", gap: "2px", flexShrink: 0 }}>
                <button
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  style={{
                    width: "22px", height: "22px", padding: 0,
                    border: "1px solid var(--border-light)",
                    borderRadius: "4px",
                    background: index === 0 ? "transparent" : "var(--bg-white)",
                    cursor: index === 0 ? "default" : "pointer",
                    fontSize: "0.65rem",
                    color: index === 0 ? "var(--border-light)" : "var(--text-dark)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                  title="Move up"
                >▲</button>
                <button
                  onClick={() => move(index, 1)}
                  disabled={index === draft.length - 1}
                  style={{
                    width: "22px", height: "22px", padding: 0,
                    border: "1px solid var(--border-light)",
                    borderRadius: "4px",
                    background: index === draft.length - 1 ? "transparent" : "var(--bg-white)",
                    cursor: index === draft.length - 1 ? "default" : "pointer",
                    fontSize: "0.65rem",
                    color: index === draft.length - 1 ? "var(--border-light)" : "var(--text-dark)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                  title="Move down"
                >▼</button>
              </div>

              {/* Icon */}
              <span style={{ fontSize: "1.2rem", flexShrink: 0 }}>{section.icon}</span>

              {/* Label + full-width badge */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: "0.88rem", fontWeight: 500, color: "var(--text-dark)" }}>
                  {section.label}
                </span>
                {section.fullWidth && (
                  <span style={{
                    marginLeft: "0.4rem",
                    fontSize: "0.7rem",
                    color: "var(--text-light)",
                    background: "var(--bg-light)",
                    border: "1px solid var(--border-light)",
                    borderRadius: "4px",
                    padding: "0 0.35rem",
                  }}>full width</span>
                )}
              </div>

              {/* Visibility toggle */}
              <div className="form-check form-switch mb-0" style={{ flexShrink: 0 }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={section.visible}
                  onChange={() => toggle(section.id)}
                  style={{ cursor: "pointer" }}
                  title={section.visible ? "Hide section" : "Show section"}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: "1rem 1.25rem",
          borderTop: "1px solid var(--border-light)",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem",
        }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={handleReset}>
            Reset to defaults
          </button>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={handleSave}>Save layout</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCustomizer;
